// Der Zuschneide-Dialog (RFC 0021).
//
// Er rechnet dieselbe Rechnung wie `Tama.Accounts.Avatar.square/3` auf dem Server: `zoom`
// = 1.0 ist das größte Quadrat, das ins Bild passt, `x`/`y` (0–1) schieben es im übrig
// gebliebenen Rand. Was hier im Kreis steht, kommt dort aus libvips heraus — deshalb steht
// die Formel an beiden Enden, und nicht die Bildpunkte.
//
// Nichts davon geht während des Ziehens an den Server: der Hook schreibt in versteckte
// Felder, und erst „Speichern" schickt drei Zahlen ab. Der Server rendert genau einmal.
export const AvatarCrop = {
  mounted() {
    this.img = this.el.querySelector("[data-crop-img]")
    this.frame = this.el.querySelector("[data-crop-frame]")
    this.zoomEl = this.el.querySelector("[data-crop-zoom]")
    this.xEl = this.el.querySelector("[data-crop-x]")
    this.yEl = this.el.querySelector("[data-crop-y]")

    this.x = clamp(parseFloat(this.xEl.value), 0, 1)
    this.y = clamp(parseFloat(this.yEl.value), 0, 1)

    // Das Bild liegt vielleicht schon im Cache — dann kommt `load` nie.
    if (this.img.complete) this.draw()
    this.img.addEventListener("load", () => this.draw())
    this.zoomEl.addEventListener("input", () => this.draw())

    // Die beiden Regler für x/y sind unsichtbar, aber bedienbar: wer nicht ziehen kann,
    // tippt sie. Dann kommt die Zahl von dort und nicht vom Finger.
    for (const el of [this.xEl, this.yEl]) {
      el.addEventListener("input", () => {
        this.x = clamp(parseFloat(this.xEl.value), 0, 1)
        this.y = clamp(parseFloat(this.yEl.value), 0, 1)
        this.draw()
      })
    }

    this.el.addEventListener("pointerdown", (e) => this.grab(e))
    this.el.addEventListener("pointermove", (e) => this.drag(e))
    this.el.addEventListener("pointerup", (e) => this.release(e))
    this.el.addEventListener("pointercancel", (e) => this.release(e))

    // Der Kreis wächst mit der Spalte: nach einer Drehung des Handys stimmt der Maßstab
    // nicht mehr.
    this.observer = new ResizeObserver(() => this.draw())
    this.observer.observe(this.frame)
  },

  destroyed() {
    this.observer?.disconnect()
  },

  // Am Bild wird nicht gezogen, sondern am Ausschnitt: schiebt der Finger nach rechts,
  // wandert der Ausschnitt nach links. Der Zoom-Regler ist ein Slider, also gehört ihm
  // sein eigener Zeiger — sonst zöge man beim Regeln das Bild mit.
  grab(e) {
    if (e.target === this.zoomEl || e.target === this.xEl || e.target === this.yEl) return
    this.from = {px: e.clientX, py: e.clientY, x: this.x, y: this.y}
    this.el.setPointerCapture(e.pointerId)
  },

  drag(e) {
    if (!this.from) return
    e.preventDefault()

    const {side, scale, width, height} = this.geometry()
    const slackX = (width - side) * scale
    const slackY = (height - side) * scale

    // Kein Spielraum, kein Verschieben: bei zoom = 1 füllt das Quadrat die kurze Kante
    // vollständig aus, und in dieser Richtung gibt es schlicht nichts zu wählen.
    this.x = slackX > 0 ? clamp(this.from.x - (e.clientX - this.from.px) / slackX, 0, 1) : 0.5
    this.y = slackY > 0 ? clamp(this.from.y - (e.clientY - this.from.py) / slackY, 0, 1) : 0.5

    this.draw()
  },

  release(e) {
    if (!this.from) return
    this.from = null
    this.el.releasePointerCapture?.(e.pointerId)
  },

  geometry() {
    const width = this.img.naturalWidth
    const height = this.img.naturalHeight
    const zoom = clamp(parseFloat(this.zoomEl.value) || 1, 1, 5)
    const side = Math.min(width, height) / zoom

    return {width, height, side, scale: this.frame.clientWidth / side}
  },

  draw() {
    const {width, height, side, scale} = this.geometry()
    if (!width || !height) return

    const left = this.x * (width - side)
    const top = this.y * (height - side)

    this.img.style.width = `${width * scale}px`
    this.img.style.height = `${height * scale}px`
    this.img.style.transform = `translate(${-left * scale}px, ${-top * scale}px)`

    // Die versteckten Felder sind das, was beim Absenden mitfährt. Sie werden bei jedem
    // Strich mitgeschrieben, damit „Speichern" nie eine andere Zahl abschickt als die,
    // die man gerade sieht.
    this.xEl.value = this.x.toFixed(4)
    this.yEl.value = this.y.toFixed(4)
  },
}

function clamp(value, lo, hi) {
  if (Number.isNaN(value)) return (lo + hi) / 2
  return Math.min(Math.max(value, lo), hi)
}
